import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Divider, Typography } from '@mui/material';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import { useNavigate } from 'react-router-dom';

import PageContainer from '../layouts/PageContainer';
import { v2Colors } from '../design/theme';
import Button from '../components/Button';
import CheckoutPageHeader from '../components/checkout/CheckoutPageHeader';
import CheckoutSectionHeading from '../components/checkout/CheckoutSectionHeading';
import CheckoutAddresses from '../components/CheckoutAddresses';
import CheckoutSlots, { slotToSummaryPayload } from '../components/CheckoutSlots';
import CheckoutPaymentSelect from '../components/CheckoutPaymentSelect';
import CheckoutStockGate from '../components/CheckoutStockGate';
import CheckoutCouponModal from '../components/CheckoutCouponModal';
import { storageGet } from '../platform/storage';
import { useV2Session } from '../state/SessionProvider';
import { paths } from '../config/paths';
import {
  V2_PAYMENT_TYPE,
  deliveryDayToYyyyMmDd,
  findOosCartLines,
  getBranchStockMap,
  getCheckoutTimeSlots,
} from '../services/checkout';
import { clearCartCoupon, getAppliedCouponDiscountRupee, getAppliedFreeSkuCouponInfo, getAppliedLoyaltyDiscountRupee, getCart, isCartLoyaltySelected, revalidateAppliedCartCoupon, setCartLoyalty } from '../services/commerce';

const LOCATION_KEY = 'v2_location';

export default function Checkout() {
  const navigate = useNavigate();
  const { mobile } = useV2Session();

  const [storeId, setStoreId] = useState(null);
  const [podId, setPodId] = useState(null);
  const [savedAddressId, setSavedAddressId] = useState(null);
  const [deliveryDay, setDeliveryDay] = useState('today');
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState('');
  const [selectedSlotId, setSelectedSlotId] = useState(null);

  const [stockMap, setStockMap] = useState({});
  const [stockLoading, setStockLoading] = useState(false);
  const [stockError, setStockError] = useState('');
  const [paymentType, setPaymentType] = useState(V2_PAYMENT_TYPE.ONLINE);
  /** Bumped after address/store merge so slots/stock refetch even when storeId is unchanged. */
  const [locationRevision, setLocationRevision] = useState(0);

  const deliveryDate = useMemo(() => deliveryDayToYyyyMmDd(deliveryDay), [deliveryDay]);

  const refreshLocationFromCache = useCallback(async () => {
    const loc = await storageGet(LOCATION_KEY);
    setStoreId(loc?.storeId ? String(loc.storeId) : null);
    const pid = loc?.podId ?? loc?.store?.podId;
    setPodId(pid != null && String(pid).trim() !== '' ? String(pid) : null);
    const aid = loc?.customerAddress?.addressId;
    setSavedAddressId(aid != null && String(aid).trim() !== '' ? String(aid) : null);
  }, []);

  useEffect(() => {
    refreshLocationFromCache();
  }, [refreshLocationFromCache]);

  const handleAfterLocationMerge = useCallback(async () => {
    await refreshLocationFromCache();
    setSelectedSlotId(null);
    setLocationRevision((n) => n + 1);
  }, [refreshLocationFromCache]);

  useEffect(() => {
    if (!storeId) {
      setSlots([]);
      setSelectedSlotId(null);
      setSlotsLoading(false);
      setSlotsError('');
      return;
    }

    let cancelled = false;
    (async () => {
      setSlotsLoading(true);
      setSlotsError('');
      try {
        const list = await getCheckoutTimeSlots({ storeId, deliveryDate, podId });
        if (cancelled) return;
        setSlots(list);
        setSelectedSlotId(null);
      } catch (e) {
        if (!cancelled) {
          setSlots([]);
          setSlotsError(e?.message || 'Could not load time slots.');
        }
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [storeId, podId, deliveryDate, locationRevision]);

  useEffect(() => {
    if (!storeId) {
      setStockMap({});
      setStockLoading(false);
      setStockError('');
      return;
    }

    let cancelled = false;
    (async () => {
      setStockLoading(true);
      setStockError('');
      try {
        const map = await getBranchStockMap(storeId);
        if (!cancelled) setStockMap(map);
      } catch (e) {
        if (!cancelled) {
          setStockMap({});
          setStockError(e?.message || 'Could not check stock.');
        }
      } finally {
        if (!cancelled) setStockLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [storeId, locationRevision]);

  const [cartSnapshot, setCartSnapshot] = useState({
    products: [],
    totalAmount: 0,
    totalCount: 0,
    coupon: null,
    instructions: '',
  });
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  const prevStoreIdRef = useRef(undefined);

  useEffect(() => {
    const prev = prevStoreIdRef.current;
    prevStoreIdRef.current = storeId;
    if (prev === undefined) return;
    if (String(prev || '') !== String(storeId || '') && cartSnapshot.coupon?.couponCode) {
      void clearCartCoupon();
    }
  }, [storeId, cartSnapshot.coupon?.couponCode]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cart = await getCart();
      if (!cancelled) setCartSnapshot(cart);
    })();
    const onCart = () => {
      getCart().then((c) => setCartSnapshot(c));
    };
    window.addEventListener('v2-cart-changed', onCart);
    return () => {
      cancelled = true;
      window.removeEventListener('v2-cart-changed', onCart);
    };
  }, []);

  useEffect(() => {
    if (!mobile || !storeId || !cartSnapshot.coupon?.couponCode) return undefined;
    let cancelled = false;
    (async () => {
      await revalidateAppliedCartCoupon({ mobile, storeId });
      if (!cancelled) {
        const c = await getCart();
        setCartSnapshot(c);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mobile, storeId, cartSnapshot.coupon?.couponCode]);

  const oosLines = useMemo(
    () => findOosCartLines(stockMap, cartSnapshot.products),
    [stockMap, cartSnapshot.products]
  );

  const hasOos = oosLines.length > 0;

  const selectedSlot = useMemo(
    () => slots.find((s) => String(s.id) === String(selectedSlotId)) ?? null,
    [slots, selectedSlotId]
  );

  const allSlotsDisabled = useMemo(
    () =>
      slots.length > 0 &&
      slots.every((s) => Boolean(s.isDisabled) || s.active === false),
    [slots]
  );

  const hasSelectedSavedAddress = Boolean(savedAddressId);
  const addressGateOk = !mobile || hasSelectedSavedAddress;

  const canContinue =
    Boolean(storeId) &&
    addressGateOk &&
    Boolean(selectedSlot) &&
    !selectedSlot.isDisabled &&
    selectedSlot.active !== false &&
    !stockLoading &&
    !stockError &&
    !hasOos;

  const subtotal = Number(cartSnapshot.totalAmount) || 0;
  const couponDiscount = getAppliedCouponDiscountRupee(cartSnapshot);
  const loyaltyDiscount = getAppliedLoyaltyDiscountRupee(cartSnapshot);
  const loyaltySelected = isCartLoyaltySelected(cartSnapshot);
  const discountedSubtotal = Math.max(0, subtotal - couponDiscount - loyaltyDiscount);
  const freeSkuCouponInfo = getAppliedFreeSkuCouponInfo(cartSnapshot);
  const hasCartItems =
    (Number(cartSnapshot.totalCount) || 0) > 0 &&
    Array.isArray(cartSnapshot.products) &&
    cartSnapshot.products.length > 0;

  const handleContinue = () => {
    if (!canContinue || !selectedSlot || hasOos) return;
    navigate(paths.checkoutSummary, {
      replace: false,
      state: {
        deliveryDate,
        timeSlot: slotToSummaryPayload(selectedSlot),
        paymentType,
        instructions: String(cartSnapshot.instructions ?? '').trim(),
      },
    });
  };

  const helperText = (() => {
    if (!storeId) return 'Select an address with delivery available.';
    if (mobile && !hasSelectedSavedAddress) return 'Choose a saved delivery address to continue.';
    if (stockLoading) return 'Checking stock…';
    if (stockError) return 'Could not verify stock. Try again or pick another address.';
    if (hasOos) return 'Update quantities or remove items in your cart to continue.';
    if (!allSlotsDisabled && !slotsLoading && !slotsError && !selectedSlotId) {
      return 'Choose a delivery slot to continue.';
    }
    return null;
  })();

  return (
    <PageContainer sx={{ px: { xs: 1.5, sm: 2 }, pb: 4 }}>
      <CheckoutPageHeader />

      <Box sx={{ mb: 2.5 }}>
        <CheckoutAddresses
          variant="checkout"
          heading="Deliver to"
          onAfterLocationMerge={handleAfterLocationMerge}
        />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <CheckoutSlots
          storeId={storeId}
          deliveryDay={deliveryDay}
          onDeliveryDayChange={(d) => {
            setDeliveryDay(d);
            setSelectedSlotId(null);
          }}
          slots={slots}
          slotsLoading={slotsLoading}
          slotsError={slotsError}
          selectedSlotId={selectedSlotId}
          onSelectSlotId={setSelectedSlotId}
        />

        {storeId ? (
          <CheckoutStockGate
            oosLines={oosLines}
            loading={stockLoading}
            error={stockError}
            onGoToCart={() => navigate(paths.cart)}
          />
        ) : null}

        <CheckoutPaymentSelect value={paymentType} onChange={setPaymentType} />

        {hasCartItems ? (
          <Box sx={{ mt: 3 }}>
            <CheckoutSectionHeading sx={{ mb: 1 }}>Order estimate</CheckoutSectionHeading>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.75 }}>
              <Typography variant="body2" color="text.secondary">
                Items subtotal
              </Typography>
              <Typography variant="body1" fontWeight={700}>
                ₹{subtotal}
              </Typography>
            </Box>
            {couponDiscount > 0 && cartSnapshot.coupon?.couponCode ? (
              <>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    mb: freeSkuCouponInfo ? 0.5 : 0.75,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Coupon ({cartSnapshot.coupon.couponCode})
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                    <Typography variant="body1" fontWeight={700} color="success.main">
                      −₹{couponDiscount}
                    </Typography>
                    <Button size="small" variant="text" onClick={() => clearCartCoupon()} sx={{ minWidth: 0, px: 0.5 }}>
                      Remove
                    </Button>
                  </Box>
                </Box>
                {freeSkuCouponInfo ? (
                  <Box
                    sx={{
                      mb: 0.75,
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: v2Colors.surface,
                      border: '1px solid',
                      borderColor: v2Colors.borderMuted,
                    }}
                  >
                    {freeSkuCouponInfo.description ? (
                      <Typography variant="body2" sx={{ color: v2Colors.textMuted, mb: 0.5, lineHeight: 1.45 }}>
                        {freeSkuCouponInfo.description}
                      </Typography>
                    ) : null}
                    <Typography variant="body2" sx={{ color: v2Colors.success, fontWeight: 600, lineHeight: 1.45 }}>
                      {freeSkuCouponInfo.autoAdded
                        ? `${freeSkuCouponInfo.productName} has been added to your cart for free.`
                        : `1 unit of ${freeSkuCouponInfo.productName} in your cart is added free with this coupon.`}
                    </Typography>
                  </Box>
                ) : null}
                <Typography variant="caption" sx={{ color: v2Colors.textMuted, display: 'block', mb: 0.75, lineHeight: 1.4 }}>
                  This coupon can’t be combined with Naatu points.
                </Typography>
              </>
            ) : null}
            {loyaltyDiscount > 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.75 }}>
                <Typography variant="body2" color="text.secondary">
                  Naatu points
                </Typography>
                <Typography variant="body1" fontWeight={700} color="success.main">
                  −₹{loyaltyDiscount}
                </Typography>
              </Box>
            ) : null}
            {couponDiscount > 0 || loyaltyDiscount > 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {couponDiscount > 0 ? 'Items after coupon' : 'Items after points'}
                </Typography>
                <Typography variant="body1" fontWeight={800}>
                  ₹{discountedSubtotal}
                </Typography>
              </Box>
            ) : null}
            <Divider sx={{ my: 1, borderColor: v2Colors.border }} />
            {!cartSnapshot.coupon?.couponCode ? (
              <Button
                variant="text"
                disabled={!storeId}
                onClick={() => setCouponModalOpen(true)}
                sx={{
                  p: 0,
                  minWidth: 0,
                  justifyContent: 'flex-start',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  textDecoration: 'underline',
                  color: v2Colors.primary,
                  mb: 0.5,
                  '&.Mui-disabled': {
                    color: v2Colors.textMuted,
                  },
                }}
              >
                Apply coupon
              </Button>
            ) : null}
            {loyaltySelected ? (
              <Typography variant="caption" sx={{ color: v2Colors.textMuted, display: 'block', mb: 1, lineHeight: 1.4 }}>
                Naatu points and coupons can’t be used together. To use a coupon, remove points in the coupon screen.
              </Typography>
            ) : null}
            <Typography variant="caption" sx={{ color: v2Colors.primaryMuted, display: 'block' }}>
              Delivery fee and final total are shown on the next step.
            </Typography>
          </Box>
        ) : null}

        <Box sx={{ mt: 3 }}>
          <Button
            fullWidth
            size="large"
            disabled={!canContinue || hasOos}
            onClick={handleContinue}
            endIcon={<ArrowForwardRounded />}
            sx={{
              borderRadius: 2,
              py: 1.5,
              fontWeight: 700,
              fontSize: '1rem',
              bgcolor: v2Colors.primary,
              '&:hover': { bgcolor: v2Colors.primaryDark },
              '&.Mui-disabled': {
                bgcolor: v2Colors.border,
                color: v2Colors.white,
              },
            }}
          >
            Continue to summary
          </Button>
          {helperText ? (
            <Typography
              variant="caption"
              display="block"
              sx={{ mt: 1, textAlign: 'center', color: v2Colors.primaryMuted }}
            >
              {helperText}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <CheckoutCouponModal
        open={couponModalOpen}
        onClose={() => setCouponModalOpen(false)}
        products={cartSnapshot.products}
        cartSubtotal={Number(cartSnapshot.totalAmount) || 0}
        storeId={storeId}
        loyaltyPoints={loyaltySelected ? Number(cartSnapshot.loyalty?.points) || 0 : 0}
        onRemoveLoyalty={async () => {
          await setCartLoyalty(null);
          setCartSnapshot(await getCart());
        }}
      />
    </PageContainer>
  );
}
